import { CreatorResponseDto } from './creators.dto';

export interface RecruitGroupResponseDto extends CreateRecruitGroupDto {
    id: string;
    numberOfCreators?: number;
    creators?: CreatorResponseDto[];
    createdDate?: Date | string;
    /** The list id Noodle minted for this group. Null until the group is synced. */
    noodleListId?: string | null;
    /**
     * Which briefs currently reference this group.
     *
     * Groups are purpose-agnostic and reusable across brief types, so the Screening /
     * Monitoring badges in My Groups are derived from THIS, not from `briefType` (which
     * only records where the group came from).
     */
    usage?: RecruitGroupUsageDto;
}

export interface RecruitGroupUsageBriefDto {
    briefId: string;
    briefTitle: string;
}

export interface RecruitGroupUsageDto {
    screeningBriefs: RecruitGroupUsageBriefDto[];
    monitoringBriefs: RecruitGroupUsageBriefDto[];
}

/** Result of adding creators to a group, as returned by POST /recruit-groups/:id/creators. */
export interface AddCreatorsToGroupResultDto {
    added: string[];
    alreadyPresent: string[];
    notFound: string[];
    /** True when the members were saved but could not be pushed to the Noodle list. */
    noodleSyncFailed?: boolean;
    /** Hand-entered rows the API could not match or create a creator for. */
    failedCreators?: { name: string; message: string }[];
}

/** A hand-entered creator with no Shout account yet; the API matches it by handle, else creates one. */
export interface AddCreatorByHandleDto {
    name?: string;
    instagramHandle?: string;
    tiktokHandle?: string;
}

export enum RecruitGroupTypesEnum {
    DEFAULT = 'default',
    CREATOR_INTEREST = 'creator_interest',
}

export enum RecruitGroupBriefTypeEnum {
    CONTENT_REVIEW = 'content_review',
    SCREENING = 'screening',
    MONITORING = 'monitoring',
}

export type RecruitGroupApprovalStatus = 'draft' | 'pending_review' | 'changes_requested' | 'approved';

export interface CreateRecruitGroupDto {
    id?: string;
    groupName: string;
    groupDescription: string;
    businessAccountId?: string;
    agencyAccountId?: string;
    createdBy: string;
    isGlobal: boolean;
    group_type?: RecruitGroupTypesEnum;
    syncWithNoodle?: boolean;
    sentToNoodle?: boolean;
    briefId?: string;
    briefType?: RecruitGroupBriefTypeEnum;
    allowReviewerInvite?: boolean;
    reviewerInviteBriefId?: string;
    invitedCreatorIds?: string[];
    shareToken?: string;
    approvalStatus?: RecruitGroupApprovalStatus;
    droppedCreatorIds?: string[];
    droppedByName?: string;
    keptCreatorIds?: string[];
    keptByName?: string;
    approvedByName?: string;
    creatorNotes?: Record<string, string>;
}

export interface RecruitGroupShareLinkDto {
    token: string;
    shareUrl?: string;
}

export interface RecruitGroupShareEmailDto {
    email: string;
    message?: string;
    shareUrl: string;
}

export interface RecruitGroupShareViewDto {
    group: RecruitGroupResponseDto;
    creators: CreatorResponseDto[];
    brandName?: string;
    canWrite?: boolean;
    shareToken: string;
    inviteBriefTitle?: string;
    invitedCreatorIds?: string[];
}

export interface RecruitGroupInviteApprovedDto {
    invitedCount: number;
    briefTitle: string;
    invitedCreatorIds?: string[];
}

export interface RecruitGroupShareReviewDto {
    groupDescription: string;
    approvalStatus?: RecruitGroupApprovalStatus;
    reviewerFirstName?: string;
}

export interface RecruitGroupsDto {
    count: number;
    data: CreateRecruitGroupDto[];
}

export interface RecruitGroupFiltersDto {
    type: 'all' | 'user' | 'business' | 'agency';
    userId?: string;
    isGlobal?: boolean;
    businessAccountId?: string;
}